<template>
  <div class="check-in__form">
    <v-flex md6 xs12>
      <v-form v-model="valid" ref="form" lazy-validation>
        <v-text-field
          label="Full name"
          name="name"
          v-model="buy.customer.full_name"
          :rules="nameRules"
          autofocus
          required
        ></v-text-field>
        <v-text-field
          label="Cell phone #"
          name="phone_number"
          v-model="buy.customer.phone_number"
          :rules="phoneRules"
          :hint="cellPhoneHint"
          type="tel"
          mask="(###) ### - ####"
          required
        ></v-text-field>
        <v-text-field
          label="Email"
          name="email"
          clearable
          v-model="buy.customer.email"
          type="email"
          @blur="handleEmailBlur"
          :rules="emailRules"
        ></v-text-field>
        <v-layout row class="align-center" v-if="showOverwriteAddress">
          <v-icon class="mr-1" v-text="`check`" />
          <span class="body-1" v-text="`Address on file`" />
          <v-btn flat @click="enableOverwriteAddress" >Edit</v-btn>
        </v-layout>
        <div v-if="showAddressFields">
          <v-text-field
            label="Address"
            name="signin_address_1"
            clearable
            v-model="buy.signin_address_1"
            type="text"
            :error-messages="computedErrors.signin_address_1"
            required
          />
          <v-text-field
            label="Suite, apartment, or other (optional)"
            name="signin_address_2"
            clearable
            v-model="buy.signin_address_2"
            type="text"
          />
          <v-text-field
            label="City"
            name="signin_city"
            clearable
            v-model="buy.signin_city"
            type="text"
            :error-messages="computedErrors.signin_city"
            required
          />
          <v-select
            autocomplete
            label="State"
            name="signin_state"
            v-model="buy.signin_state"
            :items="statesForSelect"
            :error-messages="computedErrors.signin_state"
            required
          />
          <v-text-field
            label="Postal code"
            name="signin_postal_code"
            v-model="buy.signin_postal_code"
            type="text"
            :error-messages="computedErrors.signin_postal_code"
            required
          />
        </div>
        <v-text-field
          v-if="showDriverLicenseFields"
          label="ID/Driver License Number"
          name="driver_license_number"
          v-model="driverLicense.number"
          :rules="driverLicenseNumberRules"
          required
        ></v-text-field>
        <v-select
          v-if="showDriverLicenseFields"
          label="ID/Driver License State or Province"
          name="driver_license_state"
          v-model="driverLicense.state"
          :items="statesForSelect"
          :rules="driverLicenseStateRules"
          autocomplete
          required
        ></v-select>
        <v-text-field
          v-if="showDriverLicenseFields"
          label="ID/Driver License Expiration Date"
          name="driver_license_expire_date"
          v-model="driverLicense.expire_date"
          :rules="driverLicenseExpireDateRules"
          type="date"
          required
        ></v-text-field>
        <v-btn v-if="showCancelOverwriteAddress" @click="disableOverwriteAddress">
          Use address on file
        </v-btn>
        <v-btn color="primary" @click="handleFormSubmit" :disabled="!computedValid">Next</v-btn>
      </v-form>
    </v-flex>
  </div>
</template>

<script>
import us from 'us'
import canada from 'canada'
import { mapState } from 'vuex'
import { get, compact, debounce, flatten, pick } from 'lodash'
import { isRequired, hasLength } from '@/lib/formWatchers'

export default {
  props: {
    buy: Object,
    driverLicense: Object,
    railsForm: Array,
    workingText: String,
    formErrors: Object
  },
  data () {
    return {
      valid: true,
      nameRules: [
        (v) => !!v || 'Full name is required',
        (v) => (v && !v.match(`'`)) || 'Apostrophes are not currently supported',
        (v) => (v && !v.match(',')) || 'Commas are not currently supported'
      ],
      emailRules: [],
      defaultEmailRules: [
        (v) => (/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v)) || 'Email must be a valid email address'
      ],
      driverLicenseNumberRules: [
        (v) => !!v || 'ID/Driver License number is required',
        (v) => (v && v.length >= 6) || 'Must be a valid ID number'
      ],
      driverLicenseStateRules: [
        (v) => !!v || 'ID/Driver License state is required',
        (v) => (v && this.statesForSelect.indexOf(v) > -1) || 'State must be valid'
      ],
      driverLicenseExpireDateRules: [
        (v) => !!v || 'ID/Driver License expiration date is required'
      ],
      phoneRules: [
        (v) => !!v || 'Cell phone number is required',
        (v) => (v && /\d{10}/.test(v)) || 'Cell phone number must be a valid telephone number'
      ],
      loading: false,
      success: false,
      dateInputType: 'text',
      errors: {
        signin_address_1: [],
        signin_city: [],
        signin_state: [],
        signin_postal_code: []
      },
      overwriteAddress: false,
      addressBackup: {},
      ignoreAddressValidation: false
    }
  },
  computed: {
    ...mapState([
      'winmarkStore',
      'raiStore',
      'addressIsPrepopulated'
    ]),
    // Try to determine the State/Province/Territory
    // the buy was created in
    stateFromBuy () {
      if (this.buy.state) {
        return this.lookupState(this.buy.state)
      }
      if (this.winmarkStore.id) {
        return this.lookupState(this.winmarkStore.state)
      }
      if (this.raiStore.id) {
        return this.lookupState(this.raiStore.state)
      }
      return ''
    },
    // Used for dynamically settings the email validation rules
    email () {
      let ret
      if (this.buy.customer) {
        ret = this.buy.customer.email
      } else {
        ret = ''
      }
      return ret
    },
    idRequired () {
      return get(this.raiStore, 'buy_config.buys_require_license', false)
    },
    // Old buy flow verified field
    idIsVerified () {
      return get(this.buy, 'customer.driver_license.verified', false)
    },
    // New buy flow verified field
    buyLicenseIsVerified () {
      return get(this.buy, 'license_verified_at')
    },
    showDriverLicenseFields () {
      return (this.raiStore && this.idRequired) && (!this.buyLicenseIsVerified && !this.idIsVerified)
    },
    // Returns an array of US states, Canadian provinces, and
    // canadian territories
    statesForSelect () {
      const states = us.STATES.map((s) => {
        return s.name
      })
      const provinces = Object.values(canada.provinces).map((province) => {
        return this.titleCase(province)
      })
      const territories = Object.values(canada.territories).map((territory) => {
        return this.titleCase(territory)
      })
      return states.concat(provinces).concat(territories).sort()
    },
    // Returns an object of format
    // {
    //   abbreviation: name
    // }
    statesMap () {
      const list = {}
      us.STATES.forEach((s) => { list[s.abbr] = s.name })
      Object.assign(list, canada.provinces)
      Object.assign(list, canada.territories)
      return list
    },
    buyLoaded () {
      return !!this.buy.id
    },
    cellPhoneHint () {
      return (this.buyLoaded
        ? ''
        : 'A confirmation code will be texted to this number'
      )
    },
    addressIsRequired () {
      return get(this.raiStore, 'buy_config.buys_require_address', false)
    },
    showAddressFields () {
      return (
        (this.raiStore && this.addressIsRequired) &&
        (
          !this.addressIsPrepopulated ||
          this.overwriteAddress
        )
      )
    },
    showOverwriteAddress () {
      return this.raiStore && this.addressIsRequired && this.addressIsPrepopulated && !this.overwriteAddress
    },
    showCancelOverwriteAddress () {
      return this.raiStore && this.addressIsRequired && this.addressIsPrepopulated && this.overwriteAddress
    },
    computedErrors () {
      const errors = Object.assign({}, this.errors)
      Object.keys(errors).forEach(k => {
        const anyErrors = errors[k].length > 0
        errors[k] = anyErrors ? compact(errors[k]) : []
      })
      return errors
    },
    computedValid () {
      return this.valid &&
        this.requiredFieldsAreFilled &&
        !flatten(Object.values(this.computedErrors)).some(e => e)
    },
    requiredFieldsAreFilled () {
      let allFields = [
        get(this.buy, 'customer.full_name'),
        get(this.buy, 'customer.phone_number')
      ]

      if (this.showDriverLicenseFields) {
        allFields = allFields.concat([
          get(this.driverLicense, 'number'),
          get(this.driverLicense, 'state'),
          get(this.driverLicense, 'expire_date')
        ])
      }

      if (this.showAddressFields) {
        allFields = allFields.concat([
          get(this.buy, 'signin_address_1'),
          get(this.buy, 'signin_city'),
          get(this.buy, 'signin_state'),
          get(this.buy, 'signin_postal_code')
        ])
      }
      return allFields.every(e => e)
    }
  },
  // Try to set the state field from the buy
  mounted () {
    this.autoSetState()
  },
  created () {
    this.clearNameIfNameIsPhoneNumber()
  },
  methods: {
    handleFormSubmit () {
      if (this.$refs.form.validate()) {
        window.scrollTo(0, 0)
        this.$emit('submit-form')
      }
    },
    lookupState (state) {
      let ret = this.statesMap[state]
      if (ret) return this.titleCase(ret)
      return ''
    },
    autoSetState () {
      if (this.stateFromBuy) {
        this.driverLicense.state = this.stateFromBuy
      }
    },
    // Used for dynamically setting the email rules
    handleEmailBlur () {
      if (this.email && this.email.length === 0) {
        this.emailRules = []
      }
    },
    // Takes 'CAPTAIN HOOK' and returns 'Captain Hook'
    titleCase (phrase) {
      return phrase.toLowerCase().split(' ').map((word) => {
        if (word === 'and') {
          return word
        } else {
          return word.charAt(0).toUpperCase() + word.slice(1)
        }
      }).join(' ')
    },
    clearNameIfNameIsPhoneNumber () {
      const reg = /\+1?\d{10}/
      if (reg.test(this.buy.customer.full_name)) {
        this.buy.customer.full_name = ''
      }
    },
    // Backup the existing signin address fields
    // Clear the address fields
    // Show the address fields
    enableOverwriteAddress () {
      this.backupAddressFields()
      this.clearAddressFields()
      this.overwriteAddress = true
    },
    backupAddressFields () {
      const fields = [
        'signin_address_1',
        'signin_address_2',
        'signin_city',
        'signin_state',
        'signin_postal_code'
      ]
      this.addressBackup = Object.assign({}, pick(this.buy, fields))
    },
    clearAddressFields () {
      this.ignoreAddressValidation = true
      this.buy.signin_address_1 = ''
      this.buy.signin_address_1 = ''
      this.buy.signin_address_2 = ''
      this.buy.signin_city = ''
      this.buy.signin_state = ''
      this.buy.signin_postal_code = ''
      const vm = this
      setTimeout(() => { vm.ignoreAddressValidation = false }, 1000)
    },
    // Hide the address fields
    // Restore the backup fields
    disableOverwriteAddress () {
      this.overwriteAddress = false
      this.buy = Object.assign(this.buy, this.addressBackup)
    }
  },
  watch: {
    email (v) {
      if (v && v.length > 0) {
        this.emailRules = this.defaultEmailRules
      } else {
        this.emailRules = []
      }
    },
    raiStore (v) {
      if (v.id) {
        this.autoSetState()
      }
    },
    // Debounced validations
    'buy.signin_address_1': debounce(function (v) {
      if (this.ignoreAddressValidation) return true
      const error = isRequired('Address', v)
      this.errors.signin_address_1 = error ? [error] : []
    }, 600),
    'buy.signin_city': debounce(function (v) {
      if (this.ignoreAddressValidation) return true
      const error = isRequired('City', v)
      this.errors.signin_city = error ? [error] : []
    }, 600),
    'buy.signin_state': debounce(function (v) {
      if (this.ignoreAddressValidation) return true
      const error = isRequired('State', v)
      this.errors.signin_state = error ? [error] : []
    }, 600),
    'buy.signin_postal_code': debounce(function (v) {
      if (this.ignoreAddressValidation) return true
      const requiredError = isRequired('Postal code', v)
      const lengthError = hasLength('Postal code', v, 5)
      this.errors.signin_postal_code = (requiredError || lengthError) ? [requiredError, lengthError] : []
    }, 600)
  }
}
</script>



// WEBPACK FOOTER //
// src/components/CheckInForm.vue